import { minBy, sortBy, uniqBy } from 'lodash';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Button from '~/src/components/Button';
import Spinner from '~/src/components/Spinner';
import { Urls } from '~/src/lib/enums/urls';
import { OteGame } from '~/src/models/OteGame';
import { OteGameEvent, OteGameEventScore } from '~/src/models/OteGameEvent';
import { OteGameOteTeam } from '~/src/models/OteGameOteTeam';
import { useFetchAndSubscribe } from '~/src/overtime-lib/src/www/hooks/useFetch';
import { capitalize } from '~/src/overtime-lib/src/www/lib';
import './Events.scss';

export const Events = ({
	ote_game,
	awayGameTeam,
	homeGameTeam,
}: {
	ote_game: OteGame;
	awayGameTeam: OteGameOteTeam;
	homeGameTeam: OteGameOteTeam;
}) => {
	const { id } = useParams();

	const [moreEvents, setMoreEvents] = useState<OteGameEvent[]>([]);

	const ote_game_events =
		useFetchAndSubscribe<OteGameEvent[]>({
			url: `${Urls.ApiRestOteGameBaseUrl}/${id}/ote_game_events?limit=20`,
			key: 'ote_game_events',
		}) ?? [];

	const gameEvents = sortBy(
		uniqBy(
			[...ote_game_events, ...moreEvents].filter(
				(e) =>
					e?.ote_game_id === ote_game.id &&
					![
						'clockAdjustment',
						'fixture',
						'possession',
						'possessionArrow',
						'scoreAdjustment',
						'statusAdjustment',
						'main',
					].includes(e?.event_type?.toLowerCase()) &&
					!['confirmed', 'pending'].includes(e?.sub_type?.toLowerCase()),
			),
			'id',
		),
		['event_time', 'created_at', 'id'],
	).reverse();

	const homeTeam = homeGameTeam?.ote_team;
	const awayTeam = awayGameTeam?.ote_team;

	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const [isNoMoreEvents, setIsNoMoreEvents] = useState(false);
	const getMoreEvents = () => {
		if (isFetchingMore) {
			return;
		}
		const fetchEvents = async () => {
			setIsFetchingMore(true);
			try {
				const lastEvent = minBy([...ote_game_events, ...moreEvents], 'event_time');
				const { ote_game_events: newEvents } = await (
					await fetch(
						`${Urls.ApiRestOteGameBaseUrl}/${id}/ote_game_events?limit=50&event_time_before=${lastEvent.event_time}`,
					)
				).json();
				if (newEvents.length === 0 || (newEvents.length === 1 && newEvents[0].id === lastEvent.id)) {
					setIsNoMoreEvents(true);
				} else {
					setMoreEvents((e) => uniqBy([...e, ...newEvents], 'id'));
				}
			} catch (error) {
				console.error(error);
			}
			setIsFetchingMore(false);
		};
		fetchEvents();
	};

	return (
		<div className="GameEvents">
			<div id="EventsTable" className="EventsContainer">
				<div className="Table">
					<table>
						<thead>
							<tr>
								<th style={{ textAlign: 'center' }}>Time</th>
								<th style={{ width: '2em' }}></th>
								<th style={{ width: '5em' }}>Play</th>
								<th className="PbpTeam">{awayTeam?.team_code ?? ''}</th>
								<th className="PbpTeam">{homeTeam?.team_code ?? ''}</th>
							</tr>
						</thead>
						<tbody>
							{gameEvents.map((ge) => (
								<tr key={ge.id}>
									<td className="Time">{formatClock(ge)}</td>
									<td className="TeamLogo">
										{ge.ote_team?.logo_path ? (
											<img
												src={`https://images.overtime.tv/${ge.ote_team.logo_path}?width=50&format=webp`}
												alt={ge.ote_team.name}
											/>
										) : (
											ge.ote_team?.team_code
										)}
									</td>
									<td>
										<span>
											<span className="Name">{ge.ote_player ? ge.ote_player?.full_name : ge.ote_team?.name}</span>{' '}
											{capitalize(formatWords(ge.event_type))}{' '}
											{formatWords(ge.event_type) === 'Period' ? ge.period_id : ''} {formatWords(ge.sub_type) ?? ''}{' '}
											{ge.success === true ? 'made' : ge.success === false ? 'missed' : ''}
										</span>
									</td>
									<td className="PbpScore">
										{ge.ote_game_event_scores?.find((s: OteGameEventScore) => s.ote_team_id == awayTeam.id)?.score}
									</td>
									<td className="PbpScore">
										{ge.ote_game_event_scores?.find((s: OteGameEventScore) => s.ote_team_id == homeTeam.id)?.score}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
			<div className="FetchMoreContainer">
				{isFetchingMore ? <Spinner /> : !isNoMoreEvents ? <Button onClick={getMoreEvents}>More</Button> : null}
			</div>
		</div>
	);
};

const formatClock = (ge: OteGameEvent) => {
	const clock = `${Math.floor(ge.clock / 1000 / 60)}:${String((ge.clock / 1000) % 60).padStart(2, '0')}`;
	return clock;
};

const formatWords = (word: string) => {
	if (!word) return;

	const words = word?.match(/[0-9a-zA-Z][a-z]*/g);

	if (words[0] === '1' && words[1] === '0') {
		return '10 second';
	}

	if (words[0] === 'main') {
		return 'Clock';
	}

	if (words[0] === '3' && words[1] === 'sec') {
		return '3 second';
	}

	if (words[0] === 'jump' && words[1] === 'Ball') {
		return 'Jump ball';
	}

	switch (words[0]) {
		case '5':
			return '5 second';
		case '8':
			return '8 second';
		case 'period':
			return [capitalize(words[0]), ...words.slice(1)].join();
		case 'time':
			return [capitalize(words[0]) + ' out'].join();
		default:
			return words.map((w) => w.toLowerCase()).join(' ');
	}
};

export default Events;
